import { ComponentObservable, componentObserver } from '../../component-observer';
import { currentBreakpoint, equalBreakpoints } from '../../utils/resize.utils';
import { Icon } from '../../general/icons/Icon';
import { createHtmlElementFromString } from '../../utils/html.utils';
import { filter } from '../filter/filter.module';
//import { eCatalog, ECatalogView } from '../e-catalog/e-catalog.module';
import { getUrlForImage } from '../../utils/image.utils';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { taxonomies } from '../../taxonomies.module';
import { ventilatorTaxonomyId, ventilatorTaxonomyIdMedin } from '../../api/ventilators-api';
import { allowScrollingOnBody, preventScrollingOnBody } from '../../utils/body-scroll.utils';
import { CenterModule, CenterType } from '../../utils/center.module';
import { tabOverflow } from '../tab-overflow/tab-overflow.module';
import { adaptOverviewPageForPartnernet } from '../../utils/helper.utils';
import { registerMatomoEvent } from '../../utils/matomo.utils';

/**
 * Different levels of the header.
 */
export type headerLevel = 'menu' | 'sub-menu' | 'results' | 'detail';

/**
 * Initializes the header. Make sure that there is only one header per page.
 */
export class Header implements ComponentObservable {
  /**
   * Selector of all main navigation elements that must be initialized in javascript.
   */
  componentSelector = '.section--header';

  /**
   * Current breakpoint
   */
  breakpoint = null;

  /**
   * Class added to the header if the box shadow should be hidden before scroll.
   */
  noShadowClass = 'no-shadow';

  /**
   * Height of the mobile search
   */
  searchHeight = 56;

  /**
   * Height of the main navigation
   */
  navigationHeight = '80px';

  /**
   * Height of the main navigation
   */
  navigationHeightMobile = 64;

  /**
   * Top position of the mobile menu
   */
  mobileMenuPositionTop;

  /**
   * Margin between the header and the filter.
   */
  filterMarginToHeader = 16;

  /**
   * Distance that the header is moved out of the viewport at the top on scroll. This value was selected
   * so that the header buttons (after scroll) are approximately in the middle between the top edge of the
   * viewport and the tabs of the menu
   */
  headerOffset = 54;

  /**
   * Distance that the header is moved out of the viewport (on tablet) at the top on scroll. This value was selected
   * so that the header buttons (after scroll) are approximately in the middle between the top edge of the
   * viewport and the tabs of the menu
   */
  headerOffsetTablet = 104;

  /**
   * Height of the main navigation on mobile when the search is open.
   */
  navigationAndSearchHeightMobile;

  /**
   * If the bottom shadow of the main navigation bar should be hidden until the user starts
   * scrolling on the page. If true, the shadow is hidden when the scrollTop is 0.
   */
  private hideShadowBeforeScrolled = false;

  /**
   * When the user scrolls and the main menu fades, the search, pin (etc.) buttons move to a different place and
   * get smaller. This position determines when this happens. It is the scrollTop value, e.g. px from the top.
   */
  private moveHeaderButtonsScrollPosition = null;

  /**
   * Whether this header is integrated into the eCatalog or resources center.
   */
  private isResourcesCenter = false;

  /**
   * Whether the header should hide the mobile menu.
   */
  private noMobileMenu = false;

  /**
   * The center that this header is part of (either eCatalog or resource center).
   */
  private hostingCenterModule: CenterModule;

  /**
   * Creates the template for the header
   * @param title The title that is shown in the header
   * @param labelMore The label of the more button that is shown if there are too many tabs
   * @param centerType Whether the header is used in the e-catalog, resources center or news archive
   */
  createTemplate(title: string, labelMore: string, centerType: CenterType): string {
    const isResourcesCenterOrNewsArchive =
      centerType === CenterType.RESOURCES_CENTER || centerType === CenterType.NEWS_ARCHIVE;
    const isResourcesCenter = centerType === CenterType.RESOURCES_CENTER;
    const wishListButtonId = isResourcesCenter ? 'header-button-favorites-list' : 'header-button-wishlist';
    const wishListReferenceId = isResourcesCenter
      ? 'data-favorites-list-id="favorites-list"'
      : 'data-wish-list-id="wish-list"';

    return `<section class="section section--header ${
      centerType === CenterType.RESOURCES_CENTER || centerType === CenterType.NEWS_ARCHIVE ? ' section--resources' : ''
    }">
      <div class="section__element">
          <header class="header">
              <div class="header__content">
                  <h1 class="header__title">${title}</h1>
                  <div class="header__controls">
                      <div class="dropdown-wrapper">
                        <button class="button button--dropdown" id="header-dropdown-products">
                        </button>
                      </div>
                      <div class="search-field">
                          <input class="search-field__input" id="name-input" placeholder="" />
                          <button id="search-field-remove-text-button" class="search-field__button" aria-label="Remove text" disabled></button>
                          <button class="search-field__button" aria-label="Search"></button>
                      </div>
                  </div>
                  <div class="header__buttons">
                      <button class="button button--icon-only" id="header-button-search" aria-hidden="true">
                          <span class="button__icon-single">${Icon.search}</span>
                      </button>
                      <button class="button button--icon-only" id="header-button-print">
                          <span class="button__icon-single">${Icon.print}</span>
                      </button>
                      ${
                        centerType !== CenterType.NEWS_ARCHIVE
                          ? `
                        <button class="button button--icon-only" id="${wishListButtonId}" ${wishListReferenceId}>
                          <span class="button__icon-single">${Icon.pin}</span>
                        </button>`
                          : ''
                      }
                      ${
                        isResourcesCenterOrNewsArchive
                          ? ''
                          : `<button class="button button--icon-only" id="header-button-cart" data-shopping-cart-id="shopping-cart">
                              <span class="button__icon-single">${Icon.shopping}</span>
                            </button>`
                      }
                  </div>
              </div>
              <div class="tab-overflow">
                <ul role="tablist"></ul>
                  <div class="dropdown-wrapper" style="display: none">
                    <button class="button button--dropdown" data-keep-text id="more-tabs">
                      <span class="button__text">${labelMore}</span>
                      <span class="button__icon-right">${Icon.more}</span>
                    </button>
                    <ul class="dropdown" data-reference-id="more-tabs"></ul>
                  </div>
              </div>
          </header>
      </div>
      <nav class="header__sub-navigation">
          <ul class="sub-menu"></ul>
      </nav>
  </section>`;
  }

  /**
   * Prepare the header to be integrated into a different page. This function is responsible
   * for re-initializing variables for positioning and to get a reference to the center that
   * includes the header. This reference is used to report any important user interactions
   * to the center (e.g. the eCatalog or resource center).
   */
  prepareAndReturnHeader(isResourcesCenter = false, centerModule: CenterModule, noMobileMenu = false): Header {
    this.isResourcesCenter = isResourcesCenter;
    this.noMobileMenu = noMobileMenu;
    this.hostingCenterModule = centerModule;
    this.mobileMenuPositionTop = this.navigationHeightMobile + 2;
    this.navigationAndSearchHeightMobile = this.navigationHeightMobile + this.searchHeight + 2;
    return this;
  }

  /**
   * Initialize all header elements on the current page.
   * @param observe Boolean if the HeaderModule should listen for changes in the DOM and initialize dynamically
   *   added header elements
   */
  initializeAll(observe: boolean): void {
    const allElements = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allElements.length; i++) {
      // Set an internal id to each main navigation element for mapping the intervals
      allElements[i].setAttribute('data-id', i.toString());
      this.initialize(allElements[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize header elements when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Scrolls smooth to the top to show the full header
   */
  smoothScrollToTop(): void {
    setTimeout(() => {
      window.scroll({ top: 0, behavior: 'smooth' });
    }, 50);
  }

  /**
   * Expand the reduced header. This is used for example when the user clicks on the
   * search icon or ventilator tag on the reduced header.
   */
  expandHeader(): void {
    const doc = document.documentElement;
    const scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    setTimeout(() => {
      window.scroll({ top: scrollTop - 1, behavior: 'smooth' });
    }, 50);
  }

  /**
   * Make changes on the global main navigation depending on screen size.
   * Moves the buttons to the main navigation (shopping cart, wishlist) or back, depending on screen size.
   * It also shows (desktop) or hides (mobile) the global search in the main navigation.
   * Shows the title of the header in the main navigation on mobile and moves it back on desktop.
   * @param header The header element
   * @param isDesktop Whether the current screen size is desktop or mobile
   */
  moveButtonsToMainNavigation(header: HTMLElement, isDesktop: boolean): void {
    const globalSearch = document.querySelector('#meta-link-button-search');
    const shoppingCartButton = document.querySelector('#header-button-cart');
    const wishlistButton =
      document.querySelector('#header-button-wishlist') || document.querySelector('#header-button-favorites-list');
    const mainNavigationMeta = document.querySelector('.main-navigation__meta');
    const mainNavigation = document.querySelector('.main-navigation');

    if (isDesktop) {
      const headerButtonContainer = header.querySelector('.header__buttons');
      // Move buttons from main navigation back to the header
      if (wishlistButton) {
        headerButtonContainer.appendChild(wishlistButton);
      }
      if (shoppingCartButton) {
        // append only if available (not resources center)
        headerButtonContainer.appendChild(shoppingCartButton);
      }
      // Remove empty list elements
      const liElements = mainNavigationMeta.querySelectorAll('li:empty');
      liElements.forEach((element) => element.remove());
      // Show global search
      globalSearch.parentElement.removeAttribute('aria-hidden');
    } else if (!mainNavigationMeta.querySelector('#header-button-wishlist, #header-button-favorites-list')) {
      // Shopping cart button is not available for resources center
      if (!header.classList.contains('section--resources')) {
        // Create list element with the shopping cart button and insert it to the main navigation
        const liShoppingCart = document.createElement('li');
        liShoppingCart.appendChild(shoppingCartButton);
        mainNavigationMeta.insertBefore(liShoppingCart, mainNavigationMeta.firstElementChild);
      }
      // Create list element with the wishlist button and insert it to the main navigation
      const liWishlist = document.createElement('li');
      if (wishlistButton) {
        liWishlist.appendChild(wishlistButton);
      }
      mainNavigationMeta.insertBefore(liWishlist, mainNavigationMeta.firstElementChild);
      // Hide global search
      globalSearch.parentElement.setAttribute('aria-hidden', 'true');
    }

    // Set title to header for desktop and on the main navigation for mobile
    const title = mainNavigation.querySelector('.header__title') || header.querySelector('.header__title');
    if (isDesktop) {
      const headerContent = header.querySelector('.header__content');
      headerContent.insertBefore(title, headerContent.firstElementChild);
    } else {
      mainNavigation.insertBefore(title, mainNavigation.lastElementChild);
    }
  }

  /**
   * Renders the desktop view
   * @param header The header element
   */
  renderDesktopView(header: HTMLElement): void {
    if (
      !readUrlParameter(Param.CATEGORY) &&
      !readUrlParameter(Param.PRODUCT) &&
      !readUrlParameter(Param.CUSTOM_URL) &&
      !readUrlParameter(Param.SEARCH_PHRASE) //&&
      //eCatalog.currentView !== ECatalogView.OVERVIEW
    ) {
      // if coming from mobile and no category, custom url, search phrase and no product is selected -> go to the overview page
      this.hostingCenterModule.changeHeaderMenuSelection(header, null, null);
    }

    // Move shopping cart, wishlist buttons and toggle the visibility of the global search
    this.moveButtonsToMainNavigation(header, true);

    // Get search input field
    const search = header.querySelector<HTMLElement>('.search-field');
    const input = search.querySelector('input') as HTMLInputElement;

    // Hide search button and show others
    const headerButtons = header.querySelectorAll<HTMLElement>('.header__buttons button');
    headerButtons.forEach((button) => {
      const isSearchButton = button.id === 'header-button-search';
      button.setAttribute('aria-hidden', isSearchButton.toString());
      // Set listener to search button
      if (isSearchButton) {
        button?.addEventListener('click', () => {
          // Scroll to top to show the full header
          this.expandHeader();
          // Focus the search input
          input.focus();
        });
      }
    });

    // Show tab list
    const tabList = header.querySelector('ul[role="tablist"]');
    tabList.removeAttribute('aria-hidden');

    // Set dropdown primary
    const dropdown = header.querySelector('#header-dropdown-products');
    dropdown.classList.add('button--primary');

    // Set the value of the dropdown to the tag
    const dropdownValue = header.querySelector<HTMLElement>('.button--dropdown');
    let tag = header.querySelector<HTMLElement>('.tag.tag--blue');
    // Create tag if not exists
    if (!tag) {
      const headerElement = header.querySelector('.header');
      tag = document.createElement('span');
      tag.classList.add('tag', 'tag--small', 'tag--blue');
      tag.setAttribute('role', 'button');
      tag.setAttribute('aria-hidden', 'true');
      headerElement.appendChild(tag);

      tag.addEventListener('click', () => this.expandHeader(), false);
    }
    // Set dropdown value initially
    tag.innerHTML = dropdownValue.innerText + Icon.filter;

    // Move search
    // Hide button if search field is empty
    const button = search.querySelector('button') as HTMLElement;
    // Hide button if input is empty
    if (input.value.length === 0) {
      button.removeAttribute('aria-hidden');
      button.setAttribute('disabled', 'disabled');
    }
    const headerControls = header.querySelector<HTMLElement>('.header__controls');
    headerControls.appendChild(search);
    search.classList.remove('search-field--search-button');

    // Sets the sub menu visible if available
    const subMenu = header.querySelector<HTMLElement>('.sub-menu');
    subMenu?.removeAttribute('aria-hidden');

    // Set correct position of sub navigation when switching from mobile to desktop
    const headerSubNavigation = header.querySelector<HTMLElement>('.header__sub-navigation');
    headerSubNavigation.removeAttribute('style');

    // remove the no scroll classes
    allowScrollingOnBody();
  }

  /**
   * Closes and opens the search.
   */
  toggleSearch(header: HTMLElement, close: boolean): void {
    const search = header.querySelector<HTMLElement>('.search-field');
    const searchButton = header.querySelector<HTMLElement>('#header-button-search');
    const secondLevelMenu = header.querySelector<HTMLElement>('.header__sub-navigation');
    const headerNavMobile = secondLevelMenu.querySelector('.header__navigation-mobile');
    const input = search.querySelector('input') as HTMLInputElement;
    const searchFieldIsEmpty = input.value === '';

    if (close || searchButton.classList.contains('button--active')) {
      if (searchFieldIsEmpty) {
        // only close the search field if it is empty
        searchButton.classList.remove('button--active');
        search.style.height = '0';
        if (headerNavMobile && headerNavMobile.ariaHidden !== 'true') {
          // do not set if the second level menu is not visible, otherwise it causes an overflow+scroll bar
          secondLevelMenu.style.top = `${this.mobileMenuPositionTop}px`;
          secondLevelMenu.style.bottom = '0';
        }
        search.classList.remove('search-field--search-button-open');
      }
    } else {
      searchButton.classList.add('button--active');
      search.style.height = `${this.searchHeight}px`;
      if (headerNavMobile && headerNavMobile.ariaHidden !== 'true') {
        // move the second level menu down, so that the search field is visible (+2 px border)
        // do not set if the second level menu is not visible, otherwise it causes an overflow+scroll bar
        secondLevelMenu.style.top = `${this.navigationAndSearchHeightMobile}px`;
        secondLevelMenu.style.bottom = '0';
      }
      search.classList.add('search-field--search-button-open');
      input.focus();
    }
  }

  /**
   * Renders the mobile view
   * @param header The header element
   */
  renderMobileView(header: HTMLElement): void {
    // Scroll to top to render the elements correctly
    this.smoothScrollToTop();

    // Move shopping cart, wishlist buttons and toggle the visibility of the global search
    this.moveButtonsToMainNavigation(header, false);

    const headerElement = header.querySelector('.header');
    // Show search button and hide others
    const headerButtons = header.querySelectorAll<HTMLElement>('.header__buttons button');
    const searchButton = header.querySelector<HTMLElement>('#header-button-search');
    headerButtons.forEach((button) => {
      button.setAttribute('aria-hidden', (button.id !== 'header-button-search').toString());
    });

    // Move search
    const search = header.querySelector<HTMLElement>('.search-field');
    const tabList = header.querySelector<HTMLElement>('.tab-overflow');
    headerElement.insertBefore(search, tabList);
    search.classList.add('search-field--search-button');
    // Hide button if search field is empty
    const input = search.querySelector('input') as HTMLInputElement;
    const button = search.querySelector('button') as HTMLElement;
    // Hide button if input is empty
    if (input.value.length === 0) {
      button.setAttribute('aria-hidden', 'true');
    }

    // Set dropdown primary
    const dropdown = header.querySelector('#header-dropdown-products');
    dropdown.classList.remove('button--primary');

    // Set listener to search button
    searchButton?.addEventListener(
      'click',
      (event) => {
        event?.stopImmediatePropagation();
        this.toggleSearch(header, false);
      },
      false
    );

    // Close search on remove click
    const clearSearchButton = search.querySelector<HTMLElement>('.search-field__button');
    clearSearchButton.addEventListener(
      'click',
      () => {
        this.toggleSearch(header, true);
      },
      false
    );

    // Hide search initially
    const searchOpen = header.querySelector('.search-field--search-button-open') !== null;
    this.toggleSearch(header, !searchOpen);

    // Sets the sub menu hidden
    const subMenu = header.querySelector<HTMLElement>('.sub-menu');
    subMenu.setAttribute('aria-hidden', 'true');

    // Create mobile second level navigation if not exists
    const mobileNavigation = header.querySelector('.header__navigation-mobile');
    if (!mobileNavigation) {
      this.createMobileSecondNavigation(header, subMenu);
    } else if (readUrlParameter(Param.CATEGORY)) {
      // hide the menu
      this.hideMobileMenu(header);

      // but show the filter tag
      const categoryContainer = header.querySelector('.navigation-mobile__category-container');
      categoryContainer.classList.add('navigation-mobile__category-container--filter-visible');
    }

    const isQrView = !!readUrlParameter(Param.QR_VIEW);
    const isCustomView = !!readUrlParameter(Param.CUSTOM_URL);
    const hideMobileMenu = isQrView || isCustomView;
    if (hideMobileMenu || this.noMobileMenu) {
      // if a custom URL is given -> hide the mobile menu
      this.hideMobileMenu(header);
    }

    if (isQrView) {
      // update the heading and back button for the qr view
      this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(header, null, null, 'results');
    }
  }

  /**
   * Create the mobile second navigation.
   * @param header Root element of the header
   * @param subMenu: The sub menu element. The second navigation is inserted before this element.
   */
  createMobileSecondNavigation(header: HTMLElement, subMenu: HTMLElement): void {
    const isDetailPage = this.isDetailPage();

    const mobileSecondNavigation = createHtmlElementFromString(`
      <div class="header__navigation-mobile" aria-hidden="true">
        <button class="navigation-mobile__back${this.noMobileMenu ? ' navigation-mobile__back--hidden' : ''}">${
      header.dataset.labelMobileBackToAllCategoriesButton
    }</button>
        <div class="navigation-mobile__category-container">
          <h3 class="navigation-mobile__selected-category">${
            this.noMobileMenu ? header.dataset.labelAll : ''
          }<span class="tag tag--grey${this.noMobileMenu ? ' tag--hidden' : ''}"></span></h3>
          <div class="button-section">
          <span class="tag tag--green tag--small tag--filter tag--filter-inactive">${
            header.dataset.labelMobileFilter
          } ${Icon.filter}</span>
          <span class="tag tag--green tag--small tag--filter tag--filter-secondary">${
            header.dataset.labelMobileResetFilter
          }</span>
          </div>
        </div>
      </div>
    `);

    // Open the filter when clicked on the filter button
    mobileSecondNavigation.querySelector('.tag--filter').addEventListener(
      'click',
      () => {
        filter.openFilter();
      },
      false
    );
    // reset the filter when clicked on the reset button in the mobile menu
    mobileSecondNavigation.querySelector('.tag--filter-secondary').addEventListener(
      'click',
      () => {
        this.hostingCenterModule.resetFilters();
      },
      false
    );

    subMenu.parentElement.insertBefore(mobileSecondNavigation, subMenu);
    this.initializeMobileNavigation(header);

    if (isDetailPage) {
      // if on the product detail page, hide the category container
      const categoryContainer = mobileSecondNavigation.querySelector('.navigation-mobile__category-container');
      categoryContainer.classList.add('navigation-mobile__category-container--hidden');
    }
  }

  /**
   * Hide the category header (displays title of the selected category on mobile). This is necessary for
   * the detail page where not title should be displayed.
   */
  hideCategoryHeader(): void {
    // hide the category container
    const categoryContainer = document.querySelector('.navigation-mobile__category-container');
    categoryContainer?.classList.add('navigation-mobile__category-container--hidden');
  }

  /**
   * Check if the current page is the detail page of a product/resource.
   */
  isDetailPage(): boolean {
    const productId =
      readUrlParameter(Param.PRODUCT) ||
      readUrlParameter(Param.RESOURCE_DETAIL_ID) ||
      readUrlParameter(Param.NEWS_DETAIL_ID);

    return !!productId;
  }

  /**
   * Hide the category header (displays title of the selected category on mobile). This is necessary for
   * the detail page where not title should be displayed.
   */
  hideVentilatorsAndSearchOnMobile(): void {
    // hide the category container
    const headerElement = document.querySelector('.section--header > .section__element');
    headerElement?.classList.add('section__element--hidden-mobile');
  }

  /**
   * Initializes and updates the header on breakpoint changes
   * @param header The header element
   */
  updateHeader(header: HTMLElement): void {
    if (this.breakpoint.isTablet || this.breakpoint.isDesktop) {
      this.renderDesktopView(header);
    } else {
      this.renderMobileView(header);
    }

    this.handleTabClicks(header);

    // initially show or hide the box shadow
    const doc = document.documentElement;
    const scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    this.updateHeaderShadow(header, scrollTop);
  }

  /**
   * Setter for the hideShadowBeforeScrolled property.
   */
  setHideShadowBeforeScrolled(hideShadow: boolean): void {
    this.hideShadowBeforeScrolled = hideShadow;

    // now update the current shadow
    const doc = document.documentElement;
    const scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    this.updateHeaderShadow(document.querySelector('.section--header'), scrollTop);
  }

  /**
   * Creates the dropdown element with the product values
   * @param header
   */
  async createDropdown(header: HTMLElement): Promise<void> {
    if (!header.querySelector('#header-dropdown-products .dropdown__item')) {
      const selectedDevice = header.dataset.selectedDevice || 'default_all';
      // Set all devices selected if none is specified or id does not match
      const allDevicesLabel = header.dataset.labelAllDevices;
      const dropdown = header.querySelector<HTMLElement>('#header-dropdown-products');
      dropdown.innerText = allDevicesLabel;

      const taxonomyCategories = await taxonomies.getCachedTaxonomyCategories();
      let ventilatorTaxonomy = taxonomyCategories.find((taxonomy) => taxonomy['@id'] === ventilatorTaxonomyId);

      if (window.location.hostname.indexOf('medin') > -1) {
        ventilatorTaxonomy = taxonomyCategories.find((taxonomy) => taxonomy['@id'] === ventilatorTaxonomyIdMedin);
      }

      if (ventilatorTaxonomy) {
        // Get the devices from ventilator endpoint
        const devices = [];
        const availableVentilatorNodes = ventilatorTaxonomy['@nodes'];
        const isPartnernet = window.location.href.indexOf('Partner-net') > -1;
        for (const ventilatorName of availableVentilatorNodes) {
          const ventilator = ventilatorTaxonomy[ventilatorName];

          if (ventilator.isVentilator && ventilator.partnerNetOnly === true) {
            if (!isPartnernet) continue;
          }

          if (
            ventilator.isVentilator &&
            (ventilator.markets.length === 0 || ventilator.markets.filter((m) => m.length > 2).length > 0)
          ) {
            // only nodes that have the "isVentilator" property set to true are actual ventilators
            devices.push({
              name: ventilator.name,
              image: ventilator.image?.renditions?.small?.link,
              alt: ventilator.name,
              id: ventilator['@id'],
              link: this.isResourcesCenter
                ? adaptOverviewPageForPartnernet(ventilator.ventilator?.resourceArticleLink)
                : adaptOverviewPageForPartnernet(ventilator.ventilator?.catalogProductOverviewPage)
            });
          }
        }

        let selectedDeviceLabel;
        let selectedDeviceImage;
        let items = `<li class="dropdown__item" tabindex="0" id="default_all">${allDevicesLabel}</li>`;
        devices.forEach((device) => {
          // If id matches the selected device, retrieve the name and image for the dropdown button
          if (selectedDevice === device.id) {
            selectedDeviceLabel = document.createTextNode(device.name);
            selectedDeviceImage = createHtmlElementFromString(
              `<img ${device.image ? `src="${getUrlForImage(device.image)}"` : ''} alt="${device.alt}" />`
            );
          }
          items += `<li class="dropdown__item" tabindex="0" id="${device.id}" data-link="${device.link || ''}">
          <img ${device.image ? `src="${getUrlForImage(device.image)}"` : ''} alt="${device.alt}" />${
            device.name
          }</li>`;
        });

        if (selectedDeviceLabel) {
          // if a device is present in the URL, add the name and image to the dropdown button
          dropdown.innerHTML = '';
          dropdown.appendChild(selectedDeviceImage);
          dropdown.appendChild(selectedDeviceLabel);
        }

        const dropdownItems = `<ul class="dropdown dropdown--images" data-reference-id="header-dropdown-products">
          ${items}
        </ul>`;

        dropdown.parentElement.appendChild(createHtmlElementFromString(dropdownItems));

        // Change the tag content on select
        dropdown.parentElement.lastElementChild.addEventListener('onitemselect', (event: CustomEvent) => {
          const item = event.detail.target;
          const itemId = item.id;
          const isMobile = this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape;
          const ventilatorWasSelected = itemId !== 'default_all';
          // Show custom page if user is on overview (desktop)
          if (
            !readUrlParameter(Param.TAB) &&
            !readUrlParameter(Param.CATEGORY) &&
            item.dataset.link?.length > 0 &&
            !isMobile
          ) {
            writeToUrl([
              { param: Param.CUSTOM_URL, value: item.dataset.link },
              { param: Param.VENTILATOR, value: itemId }
            ]);
            this.hostingCenterModule.changeHeaderMenuSelection(header, null, null, true);
          } else if (ventilatorWasSelected) {
            writeToUrl([
              { param: Param.VENTILATOR, value: itemId },
              { param: Param.CUSTOM_URL, value: '' }
            ]);
          } else {
            writeToUrl([], [Param.VENTILATOR]);
          }
          // Update tag
          const tag = header.querySelector('.tag--blue');
          if (tag) {
            tag.innerHTML = item.innerText + Icon.filter;
          }

          if (ventilatorWasSelected) {
            // filter results by the selected ventilator
            this.hostingCenterModule.updateFilterWithNewVentilator(itemId);
          } else {
            // don't filter results by any ventilator
            this.hostingCenterModule.updateFilterWithNewVentilator(null);
          }
        });
      }
    }
  }

  /**
   * Update the selected value of the ventilator dropdown to the ventilator with the given ID.
   */
  updateVentilatorDropdownSelection(ventilatorId: string): void {
    const header = document.querySelector('.section--header');
    const dropdown = header.querySelector('#header-dropdown-products');
    const dropdownItems = header.querySelectorAll('ul li');
    let selectedVentilatorDropdownOption = null;
    for (const item of dropdownItems) {
      if (item.id === ventilatorId) {
        selectedVentilatorDropdownOption = item;
      }
    }

    if (selectedVentilatorDropdownOption) {
      const selectedDeviceImage = selectedVentilatorDropdownOption.querySelector('img')?.cloneNode();
      const selectedDeviceLabel = document.createTextNode(selectedVentilatorDropdownOption.innerText.trim());

      dropdown.innerHTML = '';

      if(selectedDeviceImage) {
        dropdown.appendChild(selectedDeviceImage);
      }

      dropdown.appendChild(selectedDeviceLabel);

      // update the mobile tag
      const tag = header.querySelector('.tag--blue');
      if (tag) {
        tag.innerHTML = selectedVentilatorDropdownOption.innerText + Icon.filter;
      }
    }
  }

  updateMobileTabsOnSelect(header: HTMLElement): void {
    this.breakpoint = currentBreakpoint();
    const searchOpen = header.querySelector('.search-field--search-button-open');

    if (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) {
      const headerSubNavigation = header.querySelector<HTMLElement>('.header__sub-navigation');
      headerSubNavigation.style.right = '0';
      headerSubNavigation.style.bottom = '0';
      headerSubNavigation.style.opacity = '1';
      headerSubNavigation.style.pointerEvents = 'auto';
      headerSubNavigation.style.top = `${
        searchOpen ? this.navigationAndSearchHeightMobile : this.mobileMenuPositionTop
      }px`;
      headerSubNavigation.style.position = 'absolute';
    }
  }

  /**
   * Adjusts the view settings and animation for the second level navigation on mobile.
   * @param header The header element
   * @param subMenuContainer The sub menu element
   * @param isFirstLevel If the menu is on first level
   */
  updateMobileSecondLevelNavigation(header: HTMLElement, subMenuContainer: HTMLElement, isFirstLevel?: boolean): void {
    this.breakpoint = currentBreakpoint();
    const searchOpen = header.querySelector('.search-field--search-button-open');

    if (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) {
      if (isFirstLevel) {
        subMenuContainer.dataset.firstLevelOnly = 'true';
      }
      subMenuContainer.classList.add('sub-menu--second-level');
      subMenuContainer.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        subMenuContainer.setAttribute('aria-hidden', 'true');
      }, 300);
      header.style.bottom = 'auto';
      header.style.position = 'sticky';
      const headerSubNavigation = header.querySelector<HTMLElement>('.header__sub-navigation');
      headerSubNavigation.style.top = searchOpen
        ? `${this.navigationAndSearchHeightMobile}px`
        : `${this.mobileMenuPositionTop}px`;
      headerSubNavigation.style.position = 'unset';
      header.dataset.sticky = 'true';
      allowScrollingOnBody();
      this.smoothScrollToTop();
    }
  }

  /**
   * Loads the sub menu if a tab is clicked
   * @param header The header element
   */
  handleTabClicks(header: HTMLElement): void {
    if (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) {
      const subMenu = header.querySelector('.sub-menu');
      const tabs = header.querySelectorAll('.selector-item');
      const navigationMobile = header.querySelector<HTMLElement>('.header__navigation-mobile');
      tabs.forEach((tab: HTMLElement) => {
        tab.addEventListener(
          'click',
          () => {
            if (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) {
              // update the headings
              const selectedCategory = this.hostingCenterModule.getFirstLevelMenuItemFromId(tab.firstElementChild.id);
              if (selectedCategory === null) {
                // 'All' category was selected -> directly go to the search results on mobile
                this.hideMobileMenu(header);
                this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(header, null, null, 'results');
              } else {
                // A regular category was selected -> open the sub menu for the selected category
                setTimeout(() => {
                  tab.parentElement.setAttribute('aria-hidden', 'true');
                }, 300);
                subMenu.removeAttribute('aria-hidden');
                navigationMobile.removeAttribute('aria-hidden');
                this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(
                  header,
                  selectedCategory,
                  null,
                  'sub-menu'
                );
              }
            }
          },
          false
        );
      });
    }
  }

  /**
   * Hides the mobile menu
   */
  hideMobileMenu(header: HTMLElement): void {
    const tabList = header.querySelector('ul[role="tablist"]');
    const subMenu = header.querySelector<HTMLElement>('.sub-menu');
    tabList.setAttribute('aria-hidden', 'true');
    subMenu.setAttribute('aria-hidden', 'true');

    // Hide the menu/sub menu but display the filter/headings
    this.updateMobileTabsOnSelect(header);
    setTimeout(() => {
      const mobileNav = header.querySelector('.header__navigation-mobile');
      if (mobileNav) {
        mobileNav.removeAttribute('aria-hidden');
        this.updateMobileSecondLevelNavigation(header, subMenu, false);
        allowScrollingOnBody();
      }
    }, 200);
  }

  /**
   * Show or hide the sub navigation. E.g. it will be hidden on the product detail page.
   */
  showSubNav(show: boolean): void {
    const subNavigation = document.querySelector('.section--header');
    subNavigation.classList.toggle('section--header-without-sub-nav', !show);
  }

  /**
   * Moves the button from the header content to the reduced scroll view and back.
   * @param header The header element
   * @param scrollDown Whether it is scrolled down or up
   * @param scrollTop The current scrollTop position
   */
  moveButtonsOnScroll(header: HTMLElement, scrollDown: boolean, scrollTop: number): void {
    const headerElement = header.querySelector('.header');
    const headerButtons = header.querySelector('.header__buttons');
    const searchButton = headerButtons.querySelector('#header-button-search');
    const printButton = headerButtons.querySelector('#header-button-print');
    const headerContent = header.querySelector<HTMLElement>('.header__content');
    const tag = header.querySelector<HTMLElement>('.tag.tag--blue');

    if (headerElement && tag) {
      // calculate when the buttons need to move (wait for the main navigation and header to scroll out
      // of the viewport, then move the buttons)
      if (this.moveHeaderButtonsScrollPosition === null) {
        const headerOffset = this.breakpoint.isTablet ? this.headerOffsetTablet : this.headerOffset;
        // the header starts to get sticky at this position
        this.moveHeaderButtonsScrollPosition =
          (this.breakpoint.isDesktop ? parseInt(this.navigationHeight, 10) : this.navigationHeightMobile) +
          headerOffset;
      }

      // boolean if the position where the buttons should move is reached
      const buttonPositionReached = scrollTop > this.moveHeaderButtonsScrollPosition;

      if (scrollDown && buttonPositionReached) {
        searchButton.removeAttribute('aria-hidden');
        tag.removeAttribute('aria-hidden');
        printButton.setAttribute('aria-hidden', 'true');
        headerElement.insertBefore(headerButtons, tag);

        // hide the search bar and dropdown etc.
        headerContent.style.visibility = 'hidden';
      } else if (!scrollDown) {
        headerContent.appendChild(headerButtons);
        searchButton.setAttribute('aria-hidden', 'true');
        printButton.removeAttribute('aria-hidden');
        tag.setAttribute('aria-hidden', 'true');

        // show the search bar and dropdown etc.
        headerContent.removeAttribute('style');
      }
    }
  }

  /**
   * Initializes the mobile back button
   * @param header The header element
   */
  initializeMobileNavigation(header: HTMLElement): void {
    const subMenu = header.querySelector<HTMLElement>('.sub-menu');
    const tabNavigation = header.querySelector('ul[role=tablist]');
    const navigationMobile = header.querySelector<HTMLElement>('.header__navigation-mobile');
    const mobileBackButton = header.querySelector('.navigation-mobile__back');
    const headerSubNavigation = header.querySelector<HTMLElement>('.header__sub-navigation');

    // hide the mobile menu, if there is a category selection -> show results immediately
    const firstLevelItem = this.hostingCenterModule.getFirstLevelMenuItemFromId(readUrlParameter(Param.CATEGORY));
    const secondLevelItem = this.hostingCenterModule.getSecondLevelMenuItemFromId(readUrlParameter(Param.TAB));
    const isDetailPage = this.isDetailPage();

    if (firstLevelItem && !isDetailPage) {
      this.hideMobileMenu(header);
      this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(header, firstLevelItem, secondLevelItem, 'results');
    } else if (isDetailPage) {
      this.hideMobileMenu(header);
      this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(header, null, null, 'detail');
    } else {
      preventScrollingOnBody();
    }

    mobileBackButton.addEventListener('click', () => {
      // user clicked on the back button -> go back to previous level in menu
      this.handleMobileBackButtonClick(headerSubNavigation, header, subMenu, tabNavigation, navigationMobile);
      setTimeout(() => {
        if (window.localStorage && window.localStorage.getItem('scrollPosition')) {
          console.log('Scrolling to ', window.localStorage.getItem('scrollPosition'));
          window.scroll(0, parseInt(window.localStorage.getItem('scrollPosition')));
          window.localStorage.removeItem('scrollPosition');
        }
      }, 50);
    });
  }

  /**
   * Handle clicks on the back button on mobile.
   */
  handleMobileBackButtonClick(
    headerSubNavigation: HTMLElement,
    header: HTMLElement,
    subMenu: HTMLElement,
    tabNavigation: Element,
    navigationMobile: HTMLElement
  ): void {
    // fade in the category container if it was hidden before
    const categoryContainer = headerSubNavigation.querySelector('.navigation-mobile__category-container');
    categoryContainer?.classList.remove('navigation-mobile__category-container--hidden');
    header.querySelector('.section--header > .section__element')?.classList.remove('section__element--hidden-mobile');
    const product = readUrlParameter(Param.PRODUCT);
    const resource = readUrlParameter(Param.RESOURCE_DETAIL_ID);
    const selectedCategoryId = readUrlParameter(Param.CATEGORY);
    const selectedTabId = readUrlParameter(Param.TAB);

    // If coming from the qr code view -> return to the resources center overview page (except when on the detail page)
    const isQrCodeView = readUrlParameter(Param.QR_VIEW);
    if (isQrCodeView && !product) {
      // remove qr view, category and tab ids from the url
      writeToUrl([], [Param.CATEGORY, Param.TAB, Param.QR_VIEW]);
      window.location.reload();
      return;
    }

    // If on the detail page and coming from the global search -> return to it
    if ((product || resource) && !selectedCategoryId && !selectedTabId) {
      window.history.back();
      return;
    }

    // If coming from the product detail page -> don't show the menu
    if (product) {
      // remove product ID from URL
      writeToUrl([], [Param.PRODUCT]);

      const selectedCategory = this.hostingCenterModule.getFirstLevelMenuItemFromId(selectedCategoryId);
      const selectedTab = this.hostingCenterModule.getSecondLevelMenuItemFromId(selectedTabId);
      this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(header, selectedCategory, selectedTab, 'results');
      return;
    }

    if (resource) {
      // remove resource ID from URL
      writeToUrl([], [Param.RESOURCE_DETAIL_ID, Param.RESOURCE_DETAIL_TYPE]);

      const selectedCategory = this.hostingCenterModule.getFirstLevelMenuItemFromId(selectedCategoryId);
      const selectedTab = this.hostingCenterModule.getSecondLevelMenuItemFromId(selectedTabId);
      this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(header, selectedCategory, selectedTab, 'results');
      return;
    }

    const searchPhraseAndAllCategorySelected =
      readUrlParameter(Param.SEARCH_PHRASE) !== null && selectedCategoryId === 'all';

    // If header is not sticky it is the navigation's first level
    const navigateToFirstLevelMenu = header.dataset.sticky !== 'true' || subMenu.dataset.firstLevelOnly === 'true';

    if (searchPhraseAndAllCategorySelected) {
      // first show the menu
      this.showMobileMenu(header);
    }

    if (navigateToFirstLevelMenu || searchPhraseAndAllCategorySelected) {
      // go to first level menu
      subMenu.removeAttribute('data-first-level-only');
      tabNavigation.removeAttribute('aria-hidden');
      headerSubNavigation.style.right = '-120%';
      headerSubNavigation.style.opacity = '0';
      setTimeout(() => {
        headerSubNavigation.removeAttribute('style');
        navigationMobile.setAttribute('aria-hidden', 'true');
        subMenu.setAttribute('aria-hidden', 'true');
      }, 300);

      // Remove parameter from url
      writeToUrl([], [Param.CATEGORY, Param.TAB], true, true);

      // remove selected tab of menu
      const selectedTab = header.querySelector('.selector-item.selector-item--active');
      selectedTab?.classList.remove('selector-item--active');
      // select the 'all' tab
      const allTab = header.querySelector('#tab-overview')?.parentElement;
      allTab?.classList.add('selector-item--active');
    } else {
      // Second level back button with animation (go from products to second level)
      subMenu.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        headerSubNavigation.style.position = 'absolute';
        subMenu.removeAttribute('aria-hidden');
      }, 200);
      header.dataset.sticky = 'false';
      preventScrollingOnBody();
      header.removeAttribute('style');

      // update the headings
      const selectedCategoryId = tabNavigation.querySelector('.selector-item--active')?.firstElementChild?.id;
      const selectedCategory = this.hostingCenterModule.getFirstLevelMenuItemFromId(selectedCategoryId);
      this.hostingCenterModule.updateMobileMenuHeadingAndBackButton(header, selectedCategory, null, 'sub-menu');

      // Remove parameter from url
      writeToUrl([], [Param.TAB], true, true);

      // remove selected tab of sub menu
      const selectedTab = header.querySelector('.sub-menu__item.sub-menu__item--active');
      selectedTab?.classList.remove('sub-menu__item--active');
      // select the 'all' tab
      const allTab = header.querySelector('#sub-menu_all')?.parentElement;
      allTab?.classList.add('sub-menu__item--active');
      writeToUrl({ param: Param.TAB, value: 'all' });
    }

    // in both cases hide the filters
    categoryContainer.classList.remove('navigation-mobile__category-container--filter-visible');
  }

  /**
   * Fade in the mobile menu after it was previously hidden.
   */
  showMobileMenu(header: HTMLElement): void {
    const subMenu = header.querySelector<HTMLElement>('.sub-menu');
    const tabNavigation = header.querySelector('ul[role=tablist]');
    const navigationMobile = header.querySelector<HTMLElement>('.header__navigation-mobile');
    const headerSubNavigation = header.querySelector<HTMLElement>('.header__sub-navigation');

    subMenu.removeAttribute('data-first-level-only');
    tabNavigation.removeAttribute('aria-hidden');
    headerSubNavigation.style.right = '-120%';
    headerSubNavigation.style.opacity = '0';
    setTimeout(() => {
      headerSubNavigation.removeAttribute('style');
      navigationMobile.setAttribute('aria-hidden', 'true');
      subMenu.setAttribute('aria-hidden', 'true');
    }, 300);

    header.dataset.sticky = 'false';
    preventScrollingOnBody();
    header.removeAttribute('style');
  }

  /**
   * Sets the listeners for the buttons on the header
   * @param header The header element
   */
  initializeElements(header: HTMLElement): void {
    const printButton = header.querySelector('#header-button-print');
    printButton.addEventListener('click', () => {
      registerMatomoEvent('Print Button', 'Print button click', window.location.href);
      this.hostingCenterModule.printPage();
    });
  }

  /**
   * Handles the scroll behavior on scroll up / down / top and depending on screen size
   * @param scrollTop The scroll top position
   * @param lastScrollTop The last scroll top position
   * @param header The header element
   */
  handleScroll(scrollTop: number, lastScrollTop: number, header: HTMLElement): void {
    const headerContent = header.querySelector<HTMLElement>('.header__content');
    const isMobileScroll =
      (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) && header.dataset.sticky === 'true';
    // Scrolls down
    if (scrollTop > lastScrollTop && scrollTop > 0) {
      if (!isMobileScroll) {
        // Move the header buttons
        this.moveButtonsOnScroll(header, true, scrollTop);
        this.updateFilterPositionScrolledDown();
      }
      // Set header sticky and shrink it by moving out of the viewport
      const headerOffset = this.breakpoint.isTablet ? this.headerOffsetTablet : this.headerOffset;
      header.style.position = 'sticky';
      header.style.top = isMobileScroll ? '-100%' : `-${headerOffset}px`;
    } else if (scrollTop === 0) {
      if (!isMobileScroll) {
        // Show complete header, main navigation is no longer sticky
        header.removeAttribute('style');
        this.updateFilterPositionDocumentTop();
        this.moveButtonsOnScroll(header, false, scrollTop);
      }

      if (!this.breakpoint.isMobileAny) headerContent.removeAttribute('style');
    } else {
      // Scrolls up
      if (!isMobileScroll) {
        this.moveButtonsOnScroll(header, false, scrollTop);
        this.updateFilterPositionScrolledUp();
      }
      // Show complete header with offset for main navigation (that is still sticky)
      header.style.top = this.breakpoint.isDesktop ? this.navigationHeight : `${this.navigationHeightMobile}px`;
    }

    this.updateHeaderShadow(header, scrollTop);
  }

  /**
   * Show or hide the bottom shadow of the header depending on whether the user scrolled down or not.
   */
  updateHeaderShadow(header: HTMLElement, scrollTop: number): void {
    if (scrollTop === 0) {
      if (this.hideShadowBeforeScrolled) {
        // hide the shadow at the top of the page (if necessary)
        header.classList.add(this.noShadowClass);
      } else {
        header.classList.remove(this.noShadowClass);
      }
    } else {
      // always show the shadow when scrolled down
      header.classList.remove(this.noShadowClass);
    }
  }

  /**
   * Update the filter position when the user scrolled down.
   * @param includeMargin: If the position of the filter should include a marginTop to the header.
   */
  updateFilterPositionScrolledDown(includeMargin = false): void {
    const headerOffset = this.breakpoint.isTablet ? this.headerOffsetTablet : this.headerOffset;
    const headerHeightTablet = 284 - headerOffset; // height of the header+navigation minus the header offset
    const headerHeight = 236 - headerOffset; // height of the header+navigation when scrolled down, minus the header offset

    const headerHeightTabletMinusOffset = includeMargin
      ? `${headerHeightTablet + this.filterMarginToHeader}px`
      : `${headerHeightTablet}px`;
    const headerHeightMinusOffset = includeMargin
      ? `${headerHeight + this.filterMarginToHeader}px`
      : `${headerHeight}px`;

    // update the filter position, so that it sticks to the header
    const filter = document.querySelector<HTMLElement>('.filter__aside');
    const filterContent = document.querySelector<HTMLElement>('.filter__content');
    if (filter) {
      filter.style.position = 'sticky';
      const headerAndNavbarHeight = this.breakpoint.isTablet ? headerHeightTabletMinusOffset : headerHeightMinusOffset;
      filter.style.top = headerAndNavbarHeight; // height of header (minus the negative top position)

      const filterHeight = `calc(100vh - ${headerAndNavbarHeight})`; // stretch from the header to the bottom of the page
      filter.style.height = filterHeight;
      filterContent.style.minHeight = filterHeight;
    }
  }

  /**
   * Update the filter position when the user scrolled up.
   * @param includeMargin: If the position of the filter should include a marginTop to the header.
   */
  updateFilterPositionScrolledUp(includeMargin = false): void {
    const headerHeightTablet = includeMargin ? `${348 + this.filterMarginToHeader}px` : '348px';
    const headerHeight = includeMargin ? `${316 + this.filterMarginToHeader}px` : '316px'; // height of the header+navigation (when scrolled up) plus main navigation height

    // update filter position to stick to the header
    const filter = document.querySelector<HTMLElement>('.filter__aside');
    const filterContent = document.querySelector<HTMLElement>('.filter__content');
    if (filter) {
      filter.style.position = 'sticky';
      const headerAndNavbarHeight = this.breakpoint.isTablet ? headerHeightTablet : headerHeight;
      filter.style.top = headerAndNavbarHeight; // height of header+main nav bar
      const filterHeight = `calc(100vh - ${headerAndNavbarHeight})`; // stretch from the header to the bottom of the page
      filter.style.height = filterHeight;
      filterContent.style.minHeight = filterHeight;
    }
  }

  /**
   * Update the filter position when the user scrolled to the very top of the page.
   * @param includeMargin: If the position of the filter should include a marginTop to the header.
   */
  updateFilterPositionDocumentTop(includeMargin = false): void {
    const headerHeightTablet = includeMargin ? `${348 + this.filterMarginToHeader}px` : '348px';
    const headerHeight = includeMargin ? `${316 + this.filterMarginToHeader}px` : '316px'; // height of the header+navigation (when scrolled up) plus main navigation height

    // update filter position to stick to the header
    const filter = document.querySelector<HTMLElement>('.filter__aside');
    const filterContent = document.querySelector<HTMLElement>('.filter__content');
    if (filter) {
      const headerAndNavbarHeight = this.breakpoint.isTablet ? headerHeightTablet : headerHeight;
      filter.style.position = 'relative';
      const filterHeight = `calc(100vh - ${headerAndNavbarHeight})`; // stretch from the header to the bottom of the page
      filter.style.height = filterHeight;
      filterContent.style.minHeight = filterHeight;

      filter.classList.add('filter__aside--no-transition');
      window.requestAnimationFrame(() => {
        // requestAnimationFrame necessary to disable transition (otherwise the top position jumps)
        filter.style.top = '0';
        window.requestAnimationFrame(() => {
          filter.classList.remove('filter__aside--no-transition');
        });
      });
    }
  }

  /**
   * Removes the filter styles for mobile breakpoints.
   */
  resetFilter(): void {
    const isMobile = this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape;
    if (isMobile) {
      const filter = document.querySelector<HTMLElement>('.filter__aside');
      filter?.removeAttribute('style');
    }
  }

  /**
   * Initialize the given header.
   * @param header The header element that should be initialized
   */
  async initialize(header: HTMLElement): Promise<boolean> {
    return new Promise((resolve) => {
      componentObserver.markElementAsInitialized(this.componentSelector, header);
      this.breakpoint = currentBreakpoint();
      if (header.dataset.tabs) {
        this.createDropdown(header).then(() => {
          this.initializeElements(header);
          this.updateHeader(header);
          tabOverflow.initialize(header.querySelector('.tab-overflow'));
          resolve(true);
        });

        // add event listeners for the header
        this.createHeaderEventListeners(header);
      } else {
        resolve(true);
      }
    });
  }

  /**
   * Create event listeners for the header.
   */
  createHeaderEventListeners(header: HTMLElement): void {
    // Scroll listener to fade out/in header on scroll
    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollListener = (): void => {
      if (this.breakpoint.isTablet || this.breakpoint.isDesktop || header.dataset.sticky === 'true') {
        // Scroll listener to fade out/in header on scroll
        const doc = document.documentElement;
        const scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
        this.handleScroll(scrollTop, lastScrollTop, header);
        lastScrollTop = scrollTop;
      } else {
        preventScrollingOnBody();
        header.removeAttribute('style');
      }
    };

    window.addEventListener('scroll', scrollListener);
    allowScrollingOnBody();

    // timeoutId for debounce mechanism
    let timeoutId = null;
    // Set window resize listener
    const resizeListener = (): void => {
      // Prevent execution of previous setTimeout
      clearTimeout(timeoutId);
      // Change width from the state object after 500 milliseconds
      timeoutId = setTimeout(() => {
        const newBreakPoint = currentBreakpoint();
        if (!equalBreakpoints(newBreakPoint, this.breakpoint)) {
          this.smoothScrollToTop();
          this.breakpoint = newBreakPoint;
          this.moveHeaderButtonsScrollPosition = null;
          this.updateHeader(header);
          this.resetFilter();
        }
      }, 500);
    };

    // Set resize listener
    window.addEventListener('resize', resizeListener);
  }
}

const headerModule = new Header();
export { headerModule as header };
